import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { useState } from 'react';

import { FormInput, FormPanel } from 'components/FormPanel';

import SlideOver from 'primitives/SlideOver';
import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

import { generatePassword } from 'utils/auth';

const RESET_PASSWORD_MUTATION = gql(`
  mutation ResetVaultAccountPassword($password: String!, $documentVaultAccountId: ID!) {
    resetVaultAccountPassword(password: $password, documentVaultAccountId: $documentVaultAccountId) {
        id
    }
  }
`);

const DEACTIVATE_VAULT_ACCOUNT_MUTATION = gql(`
    mutation DeactivateVaultAccount($documentVaultAccountId: ID!) {
      deactivateVaultAccount(documentVaultAccountId: $documentVaultAccountId)
    }
  `);

const VaultActionSlideOver = ({
  open,
  onClose,
  documentVaultAccountId,
  accountEmail,
}: {
  open: boolean;
  onClose: (isRevoked?: boolean) => void;
  documentVaultAccountId: string;
  accountEmail: string;
}) => {
  const [password, setPassword] = useState(generatePassword(16));
  const [resetVaultAccountPassword, { loading, error }] = useMutation(RESET_PASSWORD_MUTATION);
  const [
    deactivateVaultAccount,
    { loading: deactivateVaultAccountLoading, error: deactivateVaultAccountError },
  ] = useMutation(DEACTIVATE_VAULT_ACCOUNT_MUTATION);
  const { showToast, handleToastOpen, handleToastClose } = useToast();
  const {
    showToast: showCopyToast,
    handleToastOpen: handleCopyToastOpen,
    handleToastClose: handleCopyToastClose,
  } = useToast();

  return (
    <SlideOver open={open} onClose={onClose} title="Vault Action" subTitle="Manage Vault Account">
      <div className="space-y-6">
        <div className="border-gray-300 border rounded-lg">
          <div className="p-4">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Account Email</h3>
            <p className="max-w-xl text-sm text-gray-500">{accountEmail}</p>
          </div>
        </div>
        <div className="border-gray-300 border rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Reset Password</h3>
            <p className="max-w-xl text-sm text-gray-500 mb-4">
              Generate a new password for this vault account. This will be sent to the user.
            </p>
            <FormPanel
              onSubmit={data => {
                setPassword(data.password);
                resetVaultAccountPassword({
                  variables: {
                    password: data.password,
                    documentVaultAccountId: documentVaultAccountId,
                  },
                }).then(res => {
                  handleToastOpen();
                });
              }}
              submitButtonLabel="Reset Password"
              loading={loading}
              error={error}
              onCancel={() => {
                navigator.clipboard.writeText(password);
                handleCopyToastOpen();
              }}
              cancelButtonLabel="Copy Password"
            >
              <FormInput
                type="text"
                defaultValue={password}
                fieldName="password"
                label="New Password"
              />
            </FormPanel>
          </div>
        </div>

        <div className="border-gray-300 border rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Revoke Access</h3>
            <div className="mt-2 max-w-xl text-sm text-gray-500">
              <p>Permanently revoke access to this vault account. This action cannot be undone.</p>
            </div>
            <div className="mt-5">
              <button
                onClick={() => {
                  deactivateVaultAccount({
                    variables: { documentVaultAccountId: documentVaultAccountId },
                  }).then(() => {
                    onClose(true);
                  });
                }}
                type="button"
                className="inline-flex items-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Revoke Access
              </button>
            </div>
          </div>
        </div>
      </div>
      <Toast
        type="success"
        title="Password Reset"
        message="The password has been reset successfully."
        show={showToast}
        handleClose={handleToastClose}
      />
      <Toast
        type="success"
        title="Password Copied"
        message={'The password has been copied to your clipboard.'}
        show={showCopyToast}
        handleClose={handleCopyToastClose}
      />
    </SlideOver>
  );
};

export default VaultActionSlideOver;
